import { Objective, VisibilityStatus } from "@evidenceb/gameplay-interfaces";
import React, { ReactElement, useContext } from "react";
import { Link } from "react-router-dom";
import parse from "html-react-parser";
import "./ModuleAccordion.scss";
import { defineMessages, useIntl } from "react-intl";
import { initialTestFilter } from "../../../utils/dataRetrieval";
import { configStore } from "../../../contexts/ConfigContext";
import Case from "case";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { sessionStore } from "../../../contexts/SessionContext";
/* import CacheButton from "../../../components/CacheButton/CacheButton"; */

import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { commonMessages } from "../../../utils/messages";
import useTheme from "../../../hooks/useTheme";
import Chip from "../../../design-system-components/Chip/Chip";

interface ConditionalLinkWrapperProps {
    link: string;
    wrapper: (children: ReactElement) => ReactElement;
    children: ReactElement;
}

const ObjectiveListItem = ({
    objectives,
    objective,
    index,
    link = "",
}: {
    objectives: Objective[];
    objective: Objective;
    index: number;
    link?: string;
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const {
        config: { ai },
    } = useContext(configStore);
    const { session } = useContext(sessionStore);

    const objectiveIntl = Case.pascal(intl.formatMessage(messages.objective));
    const colon = intl.formatMessage(commonMessages.colon);
    const hasInitialTest = !!objectives.find(
        initialTestFilter(ai.initialTest, "isInitialTest")
    );

    const visible = objective.visibilityStatus === VisibilityStatus.Visible;
    const unavailable =
        objective.visibilityStatus === VisibilityStatus.Unavailable;

    return (
        <div className="accordion-module-list-item__objective__content">
            <ConditionalLinkWrapper
                link={link}
                wrapper={(children: React.ReactNode) => (
                    <Link onKeyDown={handleKeydown} to={link}>
                        {children}
                    </Link>
                )}
            >
                <>
                    <span className="accordion-module-list-item__objective__title">
                        {unavailable && !session.specimen && (
                            <>
                                <Icon
                                    className="lock_icon"
                                    path="padlock"
                                    size="small"
                                    color="var(--text-main-default)"
                                />
                                <VisuallyHidden>
                                    {intl.formatMessage(
                                        messages.a11yLockedObjective
                                    )}
                                </VisuallyHidden>
                            </>
                        )}
                        {visible && (
                            <VisuallyHidden>
                                {intl.formatMessage(commonMessages.open)}
                            </VisuallyHidden>
                        )}
                        {hasInitialTest
                            ? index > 0 && `${objectiveIntl} ${index}${colon} `
                            : `${objectiveIntl} ${index}${colon} `}
                        {parse(
                            objective.descriptions
                                ? objective.descriptions.teacher.$html
                                : objective.description.$html
                        )}
                        {session.specimen && unavailable && (
                            <Chip
                                icon={{ path: "lock" }}
                                input={intl.formatMessage(
                                    messages["unavailable"]
                                )}
                                colorMode={theme}
                            ></Chip>
                        )}
                    </span>

                    {visible && link && (
                        <Icon
                            path="play_icon"
                            size="large"
                            style={{
                                color: "var(--text-main-default)",
                            }}
                            className="play_icon"
                        />
                    )}
                </>
            </ConditionalLinkWrapper>
            {/* <CacheButton
                id={objective.id}
                className="cache-button-objective"
                name={objective.title.short ?? objective.title.long}
            /> */}
        </div>
    );
};

const handleKeydown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    const target: any = e.target;
    if (target && (e.key === " " || e.key === "Enter")) {
        target.click();
    }
};

export default ObjectiveListItem;

const messages = defineMessages({
    a11yLockedObjective: {
        id: "teacher-module-list-item-a11y-lockedObjective",
        defaultMessage: "Locked",
    },
    objective: {
        id: "hierarchy-objective-full",
        defaultMessage: "objective",
    },
    unavailable: {
        id: "objective-unavailable",
        defaultMessage: "Unavailable",
    },
});

const ConditionalLinkWrapper = ({
    link,
    wrapper,
    children,
}: ConditionalLinkWrapperProps) =>
    link && link.length > 0 ? wrapper(children) : children;
